/**
 * Run Settings Tab
 *
 * Per-project configuration for test execution defaults.
 * Includes a project selector dropdown with inline project creation.
 * Free plan: max 1 project; shows upgrade prompt when limit is reached.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOrganizationFeatures } from '../../hooks/useOrganizationFeatures';
import { useProjectContext } from '../../context/ProjectContext';
import axios from 'axios';
import { GitBranch, Info, BookOpen } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Project {
    id: string;
    name: string;
    slug: string;
}

interface RunSettings {
    dockerImage: string;
    targetUrls: {
        dev: string;
        staging: string;
        prod: string;
    };
    defaultTestFolder: string;
    autoQuarantineEnabled?: boolean;
}

const EMPTY_SETTINGS: RunSettings = {
    dockerImage: '',
    targetUrls: { dev: '', staging: '', prod: '' },
    defaultTestFolder: '',
    autoQuarantineEnabled: false,
};

// ── Shared class strings ───────────────────────────────────────────────────────

const INPUT_CLASS =
    'w-full max-w-lg px-3 py-2.5 text-sm border border-slate-300 dark:border-gh-border-dark rounded-lg ' +
    'bg-white dark:bg-gh-bg-dark text-slate-900 dark:text-slate-200 ' +
    'placeholder-slate-400 dark:placeholder-slate-500 ' +
    'focus:outline-none focus:ring-2 focus:ring-gh-accent dark:focus:ring-gh-accent-dark focus:border-transparent transition ' +
    'box-border';

const SELECT_CLASS =
    'w-full max-w-lg px-3 py-2.5 text-sm border border-slate-300 dark:border-gh-border-dark rounded-lg ' +
    'bg-white dark:bg-gh-bg-dark text-slate-900 dark:text-slate-200 ' +
    'focus:outline-none focus:ring-2 focus:ring-gh-accent dark:focus:ring-gh-accent-dark focus:border-transparent transition cursor-pointer ' +
    'box-border';

// ── Component ──────────────────────────────────────────────────────────────────

export function RunSettingsTab() {
    const { token, user } = useAuth();
    const { aiFeatures, updateFeatures, isUpdating: isAiUpdating } = useOrganizationFeatures();
    const { activeProjectId, setActiveProjectId } = useProjectContext();
    const isAdmin = user?.role === 'admin';

    const [projects, setProjects] = useState<Project[]>([]);
    const selectedProjectId = activeProjectId || null;
    const setSelectedProjectId = (id: string | null) => setActiveProjectId(id ?? '');
    const [newProjectName, setNewProjectName] = useState('');
    const [creatingProject, setCreatingProject] = useState(false);
    const [projectLimitReached, setProjectLimitReached] = useState(false);
    const [projectLimitMessage, setProjectLimitMessage] = useState('');

    const [settings, setSettings] = useState<RunSettings>({ ...EMPTY_SETTINGS });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const [automationGuidelines, setAutomationGuidelines] = useState('');
    const [savingGuidelines, setSavingGuidelines] = useState(false);

    useEffect(() => { fetchProjects(); }, []);

    useEffect(() => {
        if (selectedProjectId) {
            fetchSettings(selectedProjectId);
        } else {
            setSettings({ ...EMPTY_SETTINGS });
        }
    }, [selectedProjectId]);

    useEffect(() => {
        if (!selectedProjectId || !token) {
            setAutomationGuidelines('');
            return;
        }
        axios
            .get(`${API_URL}/api/projects/${selectedProjectId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                if (res.data.success) {
                    setAutomationGuidelines(res.data.project.automationGuidelines ?? '');
                }
            })
            .catch(() => setAutomationGuidelines(''));
    }, [selectedProjectId, token]);

    async function fetchProjects() {
        try {
            const response = await axios.get(`${API_URL}/api/projects`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                const projectList: Project[] = response.data.projects;
                setProjects(projectList);
                if (projectList.length > 0 && !activeProjectId) {
                    setActiveProjectId(projectList[0].id);
                }
            }
        } catch (error: any) {
            setMessage({
                type: 'error',
                text: error.response?.data?.error || 'Failed to load projects',
            });
        } finally {
            setLoading(false);
        }
    }

    async function fetchSettings(projectId: string) {
        try {
            const response = await axios.get(`${API_URL}/api/projects/${projectId}/settings`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                setSettings(response.data.settings);
            }
        } catch {
            setSettings({ ...EMPTY_SETTINGS });
        }
    }

    async function handleCreateProject() {
        if (!newProjectName.trim()) return;

        setCreatingProject(true);
        setMessage(null);
        setProjectLimitReached(false);

        try {
            const response = await axios.post(
                `${API_URL}/api/projects`,
                { name: newProjectName.trim() },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            if (response.data.success) {
                const created = response.data.project;
                setProjects(prev => [created, ...prev]);
                setActiveProjectId(created.id);
                setNewProjectName('');
                setMessage({ type: 'success', text: `Project "${created.name}" created` });
                setTimeout(() => setMessage(null), 3000);
            }
        } catch (error: any) {
            if (error.response?.status === 403) {
                setProjectLimitReached(true);
                setProjectLimitMessage(error.response.data.error || 'Project limit reached');
            } else {
                setMessage({
                    type: 'error',
                    text: error.response?.data?.error || 'Failed to create project',
                });
            }
        } finally {
            setCreatingProject(false);
        }
    }

    async function handleSave() {
        if (!selectedProjectId) return;

        setSaving(true);
        setMessage(null);

        try {
            const response = await axios.put(
                `${API_URL}/api/projects/${selectedProjectId}/settings`,
                settings,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            if (response.data.success) {
                setSettings(response.data.settings);
                setMessage({ type: 'success', text: 'Run settings saved successfully' });
                setTimeout(() => setMessage(null), 3000);
            }
        } catch (error: any) {
            setMessage({
                type: 'error',
                text: error.response?.data?.error || 'Failed to save settings',
            });
        } finally {
            setSaving(false);
        }
    }

    async function handleSaveGuidelines() {
        if (!selectedProjectId) return;
        setSavingGuidelines(true);
        setMessage(null);
        try {
            await axios.patch(
                `${API_URL}/api/projects/${selectedProjectId}`,
                { automationGuidelines },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setMessage({ type: 'success', text: 'Automation guidelines saved' });
            setTimeout(() => setMessage(null), 3000);
        } catch (error: any) {
            setMessage({
                type: 'error',
                text: error.response?.data?.error || 'Failed to save guidelines',
            });
        } finally {
            setSavingGuidelines(false);
        }
    }

    if (loading) {
        return <p className="text-sm text-slate-500 dark:text-slate-400">Loading run settings...</p>;
    }

    return (
        <div>
            {/* Feedback banner */}
            {message && (
                <div className={`mb-4 px-4 py-3 rounded-lg text-sm border ${message.type === 'success'
                        ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                        : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
                    }`}>
                    {message.text}
                </div>
            )}

            {/* ── Project Selector ──────────────────────────────────────────────── */}
            <section className="mb-8">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">Project</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
                    Select a project to configure its test execution defaults. These settings pre-fill the Launch Modal.
                </p>

                {projects.length === 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                        No projects yet. Create your first project to configure run settings.
                    </p>
                )}

                {/* Create project row */}
                {!projectLimitReached && (
                    <div className="mb-5">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            {projects.length === 0 ? 'Create Your First Project' : 'Add Another Project'}
                        </label>
                        <div className="flex gap-2 items-center max-w-lg">
                            <input
                                type="text"
                                value={newProjectName}
                                onChange={(e) => setNewProjectName(e.target.value)}
                                placeholder="e.g. My E2E Tests"
                                className={`flex-1 min-w-0 px-3 py-2.5 text-sm border border-slate-300 dark:border-gh-border-dark rounded-lg bg-white dark:bg-gh-bg-dark text-slate-900 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-gh-accent dark:focus:ring-gh-accent-dark focus:border-transparent transition`}
                                onKeyDown={(e) => e.key === 'Enter' && handleCreateProject()}
                            />
                            <button
                                onClick={handleCreateProject}
                                disabled={creatingProject || !newProjectName.trim()}
                                className="px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-gh-bg-dark border border-slate-300 dark:border-gh-border-dark rounded-lg hover:bg-slate-50 dark:hover:bg-gh-bg-subtle-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                            >
                                {creatingProject ? 'Creating...' : 'Create'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Plan limit banner */}
                {projectLimitReached && (
                    <div className="mt-2 px-4 py-3 rounded-lg text-sm bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                        ⚡ {projectLimitMessage}.{' '}
                        <a
                            href="/settings?tab=billing"
                            className="font-semibold underline hover:no-underline"
                        >
                            Upgrade your plan
                        </a>{' '}
                        to add more projects.
                    </div>
                )}
            </section>

            {/* ── Execution Defaults ─────────────────────────────────────────────── */}
            {selectedProjectId && (
                <>
                    <hr className="border-0 border-t border-slate-200 dark:border-gh-border-dark my-7" />

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
                            Execution Defaults
                        </h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
                            These values pre-fill the Launch Modal when triggering a new test run.
                        </p>

                        {/* Project ID */}
                        <div className="mb-5">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Project ID <span className="font-normal text-slate-500 dark:text-slate-400">(Required for CI/CD API triggers)</span>
                            </label>
                            <div className="flex gap-2 items-center max-w-lg">
                                <input
                                    type="text"
                                    readOnly
                                    value={selectedProjectId}
                                    onClick={(e) => (e.target as HTMLInputElement).select()}
                                    className="flex-1 min-w-0 px-3 py-2.5 text-sm border border-slate-300 dark:border-gh-border-dark rounded-lg bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 cursor-text focus:outline-none focus:ring-2 focus:ring-gh-accent dark:focus:ring-gh-accent-dark focus:border-transparent transition select-all"
                                />
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(selectedProjectId);
                                        setMessage({ type: 'success', text: 'Project ID copied to clipboard' });
                                        setTimeout(() => setMessage(null), 3000);
                                    }}
                                    className="px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-gh-bg-dark border border-slate-300 dark:border-gh-border-dark rounded-lg hover:bg-slate-50 dark:hover:bg-gh-bg-subtle-dark transition-colors cursor-pointer whitespace-nowrap"
                                >
                                    Copy
                                </button>
                            </div>
                        </div>

                        {/* Docker Image */}
                        <div className="mb-5">
                            <label htmlFor="rs-docker-image" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Docker Image
                            </label>
                            <input
                                id="rs-docker-image"
                                data-testid="run-settings-docker-image"
                                type="text"
                                value={settings.dockerImage}
                                onChange={(e) => setSettings(prev => ({ ...prev, dockerImage: e.target.value }))}
                                placeholder="e.g. myorg/playwright-tests:latest"
                                className={INPUT_CLASS}
                            />
                        </div>

                        {/* DEV URL */}
                        <div className="mb-5">
                            <label htmlFor="rs-dev-url" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                DEV URL
                            </label>
                            <input
                                id="rs-dev-url"
                                data-testid="run-settings-dev-url"
                                type="text"
                                value={settings.targetUrls.dev}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    targetUrls: { ...prev.targetUrls, dev: e.target.value },
                                }))}
                                placeholder="e.g. https://dev.myapp.com"
                                className={INPUT_CLASS}
                            />
                        </div>

                        {/* Staging URL */}
                        <div className="mb-5">
                            <label htmlFor="rs-staging-url" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Staging URL
                            </label>
                            <input
                                id="rs-staging-url"
                                type="text"
                                value={settings.targetUrls.staging}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    targetUrls: { ...prev.targetUrls, staging: e.target.value },
                                }))}
                                placeholder="e.g. https://staging.myapp.com"
                                className={INPUT_CLASS}
                            />
                        </div>

                        {/* Production URL */}
                        <div className="mb-5">
                            <label htmlFor="rs-prod-url" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Production URL
                            </label>
                            <input
                                id="rs-prod-url"
                                type="text"
                                value={settings.targetUrls.prod}
                                onChange={(e) => setSettings(prev => ({
                                    ...prev,
                                    targetUrls: { ...prev.targetUrls, prod: e.target.value },
                                }))}
                                placeholder="e.g. https://myapp.com"
                                className={INPUT_CLASS}
                            />
                        </div>

                        {/* Default Test Folder */}
                        <div className="mb-8">
                            <label htmlFor="rs-test-folder" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                Default Test Folder
                            </label>
                            <input
                                id="rs-test-folder"
                                type="text"
                                value={settings.defaultTestFolder}
                                onChange={(e) => setSettings(prev => ({ ...prev, defaultTestFolder: e.target.value }))}
                                placeholder="e.g. tests/e2e"
                                className={INPUT_CLASS}
                            />
                        </div>

                        {/* ── Smart Analytics ──────────────────────────────────────────── */}
                        <div className="mb-6">
                            <h3 className="text-md font-semibold text-slate-900 dark:text-slate-100 mb-1">
                                Smart Analytics
                            </h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                                Enable AI-driven flakiness detection, test stability scoring, and Auto-Quarantine for continuously failing tests. (Note: Auto-Quarantine requires test executions to have a consistent 'groupName').
                            </p>

                            {/* Auto-Quarantine Toggle */}
                            <div className="flex items-center justify-between py-4 rounded-xl bg-slate-50 dark:bg-gh-bg-dark border border-slate-200 dark:border-gh-border-dark px-4 mb-5 max-w-lg">
                                <div className="flex-1 min-w-0 pr-4">
                                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                        Auto-Quarantine Flaky Tests
                                    </p>
                                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                        Automatically quarantine tests that fail 3 consecutive times to prevent them from blocking CI/CD pipelines. They will Auto-Heal on the next pass.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={settings.autoQuarantineEnabled || false}
                                    onClick={() => setSettings(prev => ({ ...prev, autoQuarantineEnabled: !prev.autoQuarantineEnabled }))}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200
                                        ${settings.autoQuarantineEnabled ? 'bg-gh-accent dark:bg-gh-accent-dark' : 'bg-slate-300 dark:bg-slate-600'}
                                        cursor-pointer`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200
                                            ${settings.autoQuarantineEnabled ? 'translate-x-6' : 'translate-x-1'}`}
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Save button */}
                        <button
                            data-testid="run-settings-submit"
                            onClick={handleSave}
                            disabled={saving}
                            className="px-5 py-2.5 text-sm font-semibold text-white bg-gh-accent dark:bg-gh-accent-dark hover:opacity-90 rounded-lg transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </section>

                    <hr className="border-0 border-t border-slate-200 dark:border-gh-border-dark my-7" />

                    {/* ── Smart PR Routing ──────────────────────────────────────────── */}
                    <section className="mb-8">
                        <div className="flex items-center gap-2 mb-1">
                            <GitBranch size={18} className="text-violet-600 dark:text-violet-400 shrink-0" />
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                Smart PR Routing
                            </h2>
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
                            Automatically select and trigger only the relevant test suite when a pull request is opened,
                            based on the files changed.
                        </p>

                        {/* Toggle row */}
                        <div className="flex items-center justify-between py-4 rounded-xl bg-slate-50 dark:bg-gh-bg-dark border border-slate-200 dark:border-gh-border-dark px-4 mb-5">
                            <div className="flex-1 min-w-0 pr-4">
                                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                    Enable Smart PR Routing
                                </p>
                                <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                                    When enabled, your CI system can call the webhook below to trigger targeted test runs.
                                </p>
                            </div>
                            {isAdmin ? (
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={aiFeatures.prRouting}
                                    disabled={isAiUpdating}
                                    onClick={() => updateFeatures({ aiFeatures: { prRouting: !aiFeatures.prRouting } })}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200
                                        ${aiFeatures.prRouting ? 'bg-gh-accent dark:bg-gh-accent-dark' : 'bg-slate-300 dark:bg-slate-600'}
                                        ${isAiUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200
                                            ${aiFeatures.prRouting ? 'translate-x-6' : 'translate-x-1'}`}
                                    />
                                </button>
                            ) : (
                                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${aiFeatures.prRouting
                                        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                                    }`}>
                                    {aiFeatures.prRouting ? 'Enabled' : 'Disabled'}
                                </span>
                            )}
                        </div>

                        {/* Webhook URL callout — always shown so users can copy */}
                        <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 text-sm">
                            <Info size={16} className="shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" />
                            <div className="min-w-0">
                                <p className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                                    Webhook Configuration
                                </p>
                                <p className="text-blue-700 dark:text-blue-300 text-xs mb-2">
                                    Configure your repository to send PR / push webhooks to the URL below.
                                    The <code className="font-mono bg-blue-100 dark:bg-blue-900/40 px-1 rounded">token</code> parameter
                                    is your Organization ID.
                                </p>
                                <code className="block text-xs font-mono bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 px-3 py-2 rounded-lg break-all select-all border border-blue-200 dark:border-blue-700">
                                    {`${API_URL}/api/webhooks/ci/pr?token=${user?.organizationId || '[YOUR_ORG_ID]'}&env=prod`}
                                </code>
                            </div>
                        </div>
                    </section>

                    <hr className="border-0 border-t border-slate-200 dark:border-gh-border-dark my-7" />

                    {/* ── Team Coding Conventions ───────────────────────────────────────── */}
                    <section className="mb-8">
                        <div className="flex items-center gap-2 mb-1">
                            <BookOpen size={18} className="text-violet-600 dark:text-violet-400 shrink-0" />
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                Team Coding Conventions
                            </h2>
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
                            Define custom coding rules for the <strong className="text-slate-700 dark:text-slate-300">Automation Planner</strong>.
                            When set, the AI will strictly follow these conventions in all generated code examples and
                            the Master AI Coding Prompt (e.g. use <code className="text-xs font-mono bg-slate-100 dark:bg-slate-800 px-1 rounded">data-testid</code> selectors,
                            prefix tests with <code className="text-xs font-mono bg-slate-100 dark:bg-slate-800 px-1 rounded">verify_</code>, etc.).
                        </p>

                        <textarea
                            value={automationGuidelines}
                            onChange={(e) => setAutomationGuidelines(e.target.value)}
                            rows={7}
                            maxLength={5000}
                            disabled={!isAdmin}
                            placeholder={`e.g.\n- Always use data-testid attributes for element selectors\n- Prefix all test names with verify_\n- Use the Page Object Model with a shared BasePage class\n- Every test must include // Arrange / // Act / // Assert comments\n- Avoid hard-coded timeouts; use auto-waiting assertions instead`}
                            className={`${INPUT_CLASS} max-w-lg resize-y font-mono text-xs leading-relaxed`}
                        />
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500 max-w-lg text-right">
                            {automationGuidelines.length} / 5000
                        </p>

                        <button
                            onClick={handleSaveGuidelines}
                            disabled={savingGuidelines || !isAdmin || !selectedProjectId}
                            className="mt-3 px-5 py-2.5 text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {savingGuidelines ? 'Saving…' : 'Save Guidelines'}
                        </button>
                    </section>
                </>
            )}
        </div>
    );
}
